import React from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';


type OperationResponse = {
  success: boolean;
  operation?: {
    id: string;
    operation_type: string;
    status: 'started' | 'in_progress' | 'completed' | 'error';
    progress: {
      percent?: number;
      step?: string | null;
      history?: Array<{
        timestamp: string;
        step: string;
        phase: 'started' | 'completed' | 'error';
        message?: string;
        details?: any;
      }>;
      result?: any;
    };
    result?: any;
    error_message?: string | null;
  };
};

const CreationErrorPage: React.FC = () => {
  const { operationId, appraisalId } = useParams<{ operationId: string; appraisalId?: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<OperationResponse['operation'] | undefined>(undefined);

  const jsonPayload = React.useMemo(() => {
    const obj = data?.result ?? data?.progress?.result ?? {};
    try { return JSON.stringify(obj, null, 2); } catch { return String(obj); }
  }, [data]);

  React.useEffect(() => {
    const load = async () => {
      if (!operationId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await authenticatedFetch(`${API_URL}/api/operations/${operationId}`, { method: 'GET' });
        if (!res || !res.ok) {
          const j = await res?.json().catch(() => ({}));
          throw new Error(j?.message || `Failed to load operation ${operationId}`);
        }
        const j: OperationResponse = await res.json();
        setData(j.operation);
      } catch (e: any) {
        setError(e?.message || 'Failed to load operation');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authenticatedFetch, operationId]);

  const handleBreadcrumb = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    e.preventDefault();
    navigate(to);
  };

  const handleCopy = () => {
    if (navigator?.clipboard) navigator.clipboard.writeText(jsonPayload);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link color="inherit" href="/" onClick={(e) => handleBreadcrumb(e, '/')} sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Home</Link>
        <Link color="inherit" href="/appraisals" onClick={(e) => handleBreadcrumb(e, '/appraisals')} sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Appraisals</Link>
        {appraisalId ? (
          <Link color="inherit" component={RouterLink} to={`/appraisals/${appraisalId}/edit`} sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Appraisal {appraisalId}</Link>
        ) : null}
        <Typography color="text.primary">Creation Error</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
          Appraisal Creation Error
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review the details below to troubleshoot the orchestration.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !data ? (
        <Alert severity="warning">No operation data found.</Alert>
      ) : (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Operation ID</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{data.id}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Type</Typography>
              <Typography variant="body2">{data.operation_type}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Status</Typography>
              <Typography variant="body2">{data.status}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Percent</Typography>
              <Typography variant="body2">{data.progress?.percent ?? 0}%</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Current Step</Typography>
              <Typography variant="body2">{data.progress?.step || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary">Appraisal ID</Typography>
              <Typography variant="body2">{data.progress?.result?.appraisalId ?? data.result?.appraisalId ?? '-'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Error Message</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{data.error_message || '-'}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" sx={{ mb: 1 }}>History</Typography>
          {Array.isArray(data.progress?.history) && data.progress!.history!.length > 0 ? (
            <List dense>
              {data.progress!.history!.map((h, idx) => (
                <ListItem key={idx} alignItems="flex-start" divider>
                  <ListItemText
                    primary={`${h.timestamp} — ${h.step} [${h.phase}]`}
                    secondary={h.message || (h.details ? JSON.stringify(h.details) : undefined)}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">No history.</Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ flex: 1 }}>Result Payload</Typography>
            <Tooltip title="Copy to clipboard">
              <IconButton size="small" onClick={handleCopy}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {jsonPayload}
            </Typography>
          </Paper>
        </Paper>
      )}
    </Container>
  );
};

export default CreationErrorPage;


